import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { useTranslation } from '@sunbit-dev/translations';
import { ContentProps } from './models';
import * as styles from './styles';
import { useTrack } from '~/hooks/track';
import { ButtonSize, StyledLink, StyledLink as SLink } from '@sunbit-dev/web-ui-lib';
import { EventsNames } from '~/enums/Events';
import { ActionButton } from '@sunbit-dev/purchase-common/src/components/actionButton/ActionButton';
import { useMediaQuery } from 'react-responsive';
import { Size } from '@sunbit-dev/purchase-common/src/theme/defaultTheme';
import { Slogan } from './components/slogan/Slogan';
import { HealthcareActionContext } from '~/contexts/HealthcareContext';
import { AppContext } from '~/contexts/appContext';
import infoImage from '~/assets/info-outline-light.svg';
import { documentUrls } from '~/helpers/urlHelper';

export const Content = styled(({ className }: ContentProps) => {
  const { t } = useTranslation('healthcare_landing_modal');
  const { track } = useTrack();
  const { setShouldDisplayNotice } = React.useContext(HealthcareActionContext);
  const { retailer } = React.useContext(AppContext);

  const isMobile = useMediaQuery({
    query: `(max-width: ${Size.mobileXL}px)`,
  });

  const rootDiv = useRef<HTMLDivElement | undefined>(undefined);
  const legalContentEnd = useRef<HTMLDivElement | undefined>(undefined);

  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(false);
  const [shouldDisplayLegalScrollNotice, setShouldDisplayLegalScrollNotice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { privacyPolicyUrl, termsOfUseUrl } = documentUrls(retailer) || {};

  const toEmailLink = ({ text }) => <SLink {...{ className: 'link', href: 'mailto:support@sunbit.com' }}>{text}</SLink>;

  useEffect(() => {
    if (hasReachedBottom) setShouldDisplayLegalScrollNotice(false);
  }, [hasReachedBottom]);

  const onSubmit = useCallback(() => {
    setShouldDisplayNotice(false);
  }, []);

  const onSubmitClick = useCallback(() => {
    if (!isMobile || (isMobile && hasReachedBottom)) {
      setIsSubmitting(true);
      onSubmit();
    } else if (isMobile && !hasReachedBottom) {
      setShouldDisplayLegalScrollNotice(true);
    }
  }, [onSubmit, hasReachedBottom, isMobile]);

  useEffect(() => {
    if (isMobile && rootDiv?.current && legalContentEnd?.current && !isScrollListenerActive) {
      setIsScrollListenerActive(true);

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setHasReachedBottom(true);
          }
        },
        { threshold: 1 }
      );

      observer.observe(legalContentEnd?.current);
    }
  }, [isMobile]);

  useEffect(() => {
    track({ eventName: EventsNames.HealthcareNotice });
  }, []);

  const legalFooter = useMemo(() => {
    const legalFooter: string[] = t('legal_footer');

    return (
      <>
        <div className="spacer"></div>
        <div className="legal-notice">
          {legalFooter[0]}
          <SLink className="link" data-testid="rates-and-terms" href={termsOfUseUrl} target="_blank">
            {legalFooter[1]}
          </SLink>
          {legalFooter[2]}
        </div>
      </>
    );
  }, [t]);

  const welcomeActionButton = useMemo(() => {
    const isDisableForLegalText = isMobile && !hasReachedBottom;

    const actionButton = (
      <ActionButton
        className="submit-button"
        dataTestId="submit-button"
        size={ButtonSize.large}
        buttonText={t('submit_button')}
        submit={onSubmit}
        dataDdAction={'EventsNames.submitPage'}
        disabled={isSubmitting || (isMobile && !hasReachedBottom)}
      />
    );

    return isDisableForLegalText ? (
      <div className="submit-button-container" onClick={onSubmitClick}>
        {actionButton}
        <div className="overlay"></div>
      </div>
    ) : (
      actionButton
    );
  }, [isMobile, hasReachedBottom, onSubmitClick, isSubmitting, onSubmit]);

  const legalConsent = t('legal_consent');

  return (
    <div className={classNames(className)} ref={rootDiv}>
      <Slogan className="slogan" />
      <h2 className="title">{t('title', { merchantName: retailer.merchantName })}</h2>
      <p className="legal-consent">
        {legalConsent[0]}
        <SLink className="link" data-at="privacy-policy-link" href={privacyPolicyUrl} target="_blank">
          {legalConsent[1]}
        </SLink>
        {legalConsent[2]}
        {toEmailLink({ text: legalConsent[3] })}
        {legalConsent[4]}
      </p>
      {!isMobile && (
        <div className="submit-button-wrapper">
          <ActionButton
            className="submit-button"
            dataTestId="submit-button"
            size={ButtonSize.large}
            buttonText={t('submit_button')}
            submit={onSubmit}
            dataDdAction={'EventsNames?.submitPage'}
          />
        </div>
      )}
      {legalFooter}
      <div ref={legalContentEnd} className="legal-content-end"></div>
      {isMobile && (
        <>
          <div className="sticky-button-wrapper-placeholder" />
          <div className={classNames(className, 'mobile-bottom-container')}>
            <div className="sticky-bottom-content">
              {shouldDisplayLegalScrollNotice && (
                <div className="scroll-notice">
                  <img className="info-icon" src={infoImage} />
                  {t('scroll_to_bottom_notice')}
                </div>
              )}
              <div className="sticky-button-wrapper">{welcomeActionButton}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
})`
  ${styles.Content}
`;
